/**
 * NotificationItem Component
 * Individual notification display with actions
 */

import React from 'react';
import { useNotifications } from '../../context/NotificationContext';
import './NotificationItem.css';

const NotificationItem = ({ notification }) => {
  const { markAsRead, markAsAcknowledged, deleteNotification } = useNotifications();

  const {
    _id,
    title,
    message,
    type = 'info',
    priority = 'normal',
    isRead,
    isAcknowledged,
    createdAt,
    actionUrl,
    actionLabel,
  } = notification;

  const handleMarkAsRead = async (e) => {
    e.stopPropagation();
    if (!isRead) {
      await markAsRead(_id);
    }
  };

  const handleAcknowledge = async (e) => {
    e.stopPropagation();
    if (!isAcknowledged) {
      await markAsAcknowledged(_id);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this notification?')) {
      await deleteNotification(_id);
    }
  };

  const handleAction = (e) => {
    e.stopPropagation();
    if (actionUrl) {
      window.open(actionUrl, '_blank');
      if (!isRead) {
        markAsRead(_id);
      }
    }
  };

  const getTypeIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        );
      case 'warning':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        );
      case 'error':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        );
      case 'system':
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        );
      default: // info
        return (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        );
    }
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const created = new Date(date);
    const seconds = Math.floor((now - created) / 1000);

    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return created.toLocaleDateString();
  };

  return (
    <div
      className={`notification-item ${type} ${priority} ${isRead ? 'read' : 'unread'} ${isAcknowledged ? 'acknowledged' : ''}`}
      onClick={handleMarkAsRead}
    >
      <div className="notification-item-icon">
        {getTypeIcon()}
      </div>

      <div className="notification-item-content">
        <div className="notification-item-header">
          <h4 className="notification-item-title">{title}</h4>
          <span className="notification-item-time">{getTimeAgo(createdAt)}</span>
        </div>

        <p className="notification-item-message">{message}</p>

        <div className="notification-item-actions">
          {actionUrl && actionLabel && (
            <button
              className="notification-item-action-button primary"
              onClick={handleAction}
            >
              {actionLabel}
            </button>
          )}

          {!isRead && (
            <button
              className="notification-item-action-button"
              onClick={handleMarkAsRead}
              title="Mark as read"
            >
              Mark as read
            </button>
          )}

          {!isAcknowledged && (
            <button
              className="notification-item-action-button"
              onClick={handleAcknowledge}
              title="Acknowledge"
            >
              Acknowledge
            </button>
          )}

          <button
            className="notification-item-action-button delete"
            onClick={handleDelete}
            title="Delete"
            aria-label="Delete notification"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      </div>

      {!isRead && <div className="notification-item-unread-indicator" />}
    </div>
  );
};

export default NotificationItem;
